// Example service: filepath: /home/boss/workspace/pwa/zgrana/src/app/services/example.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GenericResponseWithTotal } from '../interfaces/generic.interface';
import { Partner } from '../interfaces/partners.interface';
import { Person, EditPerson } from '../interfaces/persons.interface';
import { File as AppFile } from '../interfaces/files.interface';
import { DataProcessingService } from './data-prcessing.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, shareReplay } from 'rxjs/operators';
import { UsersService } from './users.service';
import { LoggerService } from './logger.service';

@Injectable({ providedIn: 'root' })
export class PersonsService { 
  
  public current$ = new BehaviorSubject<Person[] | null>(null);
  private currentPersonsCache: Person[] | null = null;
  private cacheTimestamp: number | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  
  private get apiUrl(): string {
    return this.loggerService.resolveApiUrl('persons');
  }


  constructor(
    private http: HttpClient,
    private dataProcess: DataProcessingService,
    private usersService: UsersService,
    private loggerService: LoggerService
  ) {
    // Clear cache when user logs in or out
    this.usersService.loggedIn.subscribe(isLoggedIn => {
      this.clearCache();
    });
  }

  getCurrent(nocache = false): Observable<Person[]> {
    // Check if cache is valid
    if (!nocache && this.currentPersonsCache && this.isCacheValid()) {
      return of(this.currentPersonsCache);
    }

    return this.http.get<Person[]>(this.apiUrl + '/current').pipe(
      tap(persons => {
        this.currentPersonsCache = persons;
        this.cacheTimestamp = Date.now();
      })
    );
  }

  private isCacheValid(): boolean {
    if (!this.cacheTimestamp) return false;
    return (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION;
  }

  private clearCache(): void {
    this.currentPersonsCache = null;
    this.cacheTimestamp = null;
  }

  updatePerson(id: number, data: EditPerson): Observable<Person> {
    return this.http.put<Person>(`${this.apiUrl}/${id}`, data).pipe(
      tap((response) => {
        this.clearCache();
      })
    );
  }

  getPersonsByUser(user_id): Observable<Person[]> {
    return this.http.get<Person[]>(this.apiUrl + '/user/' + user_id);
  }

  getData(id: number): Observable<Person> {
    return this.http.get<Person>(this.apiUrl + '/' + id);
  }

  getFiles(personId: number): Observable<AppFile[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${personId}/files`);
  }

  getFileContent(personId: number, uuid: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${personId}/files/${uuid}`, {
      responseType: 'blob'
    });
  }

  createPerson(data: Partial<EditPerson>): Observable<Person> {
    return this.http.post<Person>(this.apiUrl, data).pipe(
      tap(() => this.clearCache()) // Clear cache after creation
    );
  }

  checkIfExistsPerson(data: Partial<EditPerson>): Observable<Person | null> {
    return this.http.post<Person | null>(`${this.apiUrl}/check-if-exist`, data);
  }

  deletePerson(personId: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${personId}`).pipe(
      tap(() => this.clearCache()) // Clear cache after deletion
    );
  }

  deleteFile(personId: number, uuid: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${personId}/files/${uuid}`);
  }
  
  uploadFile(personId: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(`${this.apiUrl}/${personId}/upload`, formData);
  }

  getPersons(params?: {
    order_by?: string;
    order?: string;
    skip?: number;
    limit?: number;
    [key: string]: any; // for filters
  }): Observable<GenericResponseWithTotal<Person>> {
    return this.http.get<GenericResponseWithTotal<Person>>(this.apiUrl, { params: this.dataProcess.fitlerParams(params) });
  }

}